import axios from 'axios';
import { setAlert } from './alert';

const API_URL = process.env.REACT_APP_SERVER_API_URL;

const TEST_FLOWS_URL = `${API_URL}/whatsapp-test-flows`;

const request = ({ method, path, params, data, fallback }) => async dispatch => {
    try {
        const res = await axios({ method, url: `${TEST_FLOWS_URL}${path}`, params, data });
        if (res.data.success) return res.data.data;
        dispatch(setAlert(res.data.error || fallback, 'danger'));
        return null;
    } catch (err) {
        console.error(fallback, err);
        dispatch(setAlert(err.response?.data?.error || fallback, 'danger'));
        return null;
    }
};

/**
 * Flow catalogue with the buttons each flow offers
 * @returns {Promise<Array|null>} null after an alert on failure
 */
export const getTestFlows = () => request({
    method: 'get',
    path: '/flows',
    fallback: 'Failed to load the WhatsApp test flows'
});

/**
 * Customers matching a name or phone, newest first (admins are never returned)
 * @param {string} [search] - Part of a name or phone; empty returns the newest customers
 * @returns {Promise<Array|null>} null after an alert on failure
 */
export const searchTestCustomers = (search = '') => request({
    method: 'get',
    path: '/users',
    params: { search },
    fallback: 'Failed to search customers'
});

/**
 * Send one flow to a customer, or record it as a dry run
 * @param {Object} payload
 * @param {string} payload.userId - Customer id
 * @param {string} payload.stage - Flow stage key
 * @param {boolean} payload.dryRun - true records the message without sending it
 * @returns {Promise<Object|null>} outcome of the send, or null after an alert on failure
 */
export const triggerTestFlow = ({ userId, stage, dryRun }) => request({
    method: 'post',
    path: '/trigger',
    data: { userId, stage, dryRun },
    fallback: 'Failed to trigger the WhatsApp flow'
});

/**
 * Run a button tap or number reply through the real handler; the reply is returned, not sent
 * @param {Object} payload
 * @param {string} payload.userId - Customer id
 * @param {string} payload.key - Rule key such as get_offer or num_2
 * @returns {Promise<{key: string, reply: string, events: Array}|null>} null after an alert on failure
 */
export const simulateTestTap = ({ userId, key }) => request({
    method: 'post',
    path: '/tap',
    data: { userId, key },
    fallback: 'Failed to simulate the tap'
});

/**
 * Remove everything a customer did so they behave like a newly registered user (stage only)
 * @param {Object} payload
 * @param {string} payload.userId - Customer id
 * @param {string} payload.confirmPhone - The customer phone, typed back as a confirmation
 * @param {boolean} payload.removeAddresses - true also removes the saved delivery addresses
 * @returns {Promise<{deleted: Object, failed: string[]}|null>} null after an alert on failure
 */
export const nukeTestCustomer = ({ userId, confirmPhone, removeAddresses }) => request({
    method: 'post',
    path: '/nuke',
    data: { userId, confirmPhone, removeAddresses },
    fallback: 'Failed to reset the customer'
});

/**
 * Enrollments, open conversation, recent messages and events of a customer
 * @param {string} userId - Customer id
 * @returns {Promise<Object|null>} null after an alert on failure
 */
export const getTestFlowState = (userId) => request({
    method: 'get',
    path: '/state',
    params: { userId },
    fallback: 'Failed to load the test customer'
});
